/**
 * 工具函数：处理流式生成内容
 * @param {string} API_BASE_URL API基础URL
 * @param {object} params 请求参数
 * @param {string} section 内容类型标识
 * @param {Function} onChunkReceived 内容块接收回调 (chunk, fullContent, wordCount) => void
 * @param {Function} onStatusUpdate 状态更新回调 (message) => void
 * @param {Function} [onThinkingChunk] 思考内容接收回调 (thinkingContent) => void，当AI正在推理时会收到思考内容
 * @param {string} [streamPath='/ai/proxy-ai'] API路径
 */

/** 辅助创作流式请求完成后通知界面刷新「今日剩余次数」（与 axios 非流式路径一致） */
function emitAssistAiQuotaRefresh(streamPath) {
  if (streamPath && String(streamPath).includes('/assist/ai')) {
    try {
      window.dispatchEvent(new CustomEvent('assistAiQuotaRefresh'));
    } catch (_) {}
  }
}

/**
 * 验证生成内容的完整性（仅用于日志记录，不再用于触发重试）
 * @param {string} content 生成的内容
 * @param {string} section 部分名称（'FULL_OUTLINE'、'第一部分'、'第二部分'或'第三部分'）
 * @returns {boolean} 内容是否完整
 */
export const verifyContentCompleteness = (content, section) => {
  if (!content) return false;

  // 一次性完整大纲：检查所有三部分的主要内容模块
  if (section === 'FULL_OUTLINE') {
    return content.includes('【原创性声明') &&
           content.includes('【世界观体系设计】') &&
           content.includes('【人物塑造系统】') &&
           content.includes('【剧情发展系统】') &&
           content.includes('【结局设计】');
  }

  // 检查是否包含必要的部分
  if (section === '第二部分') {
    return content.includes('【开篇设计】') &&
           content.includes('【剧情发展系统】') &&
           content.includes('【人物成长体系】');
  } else if (section === '第三部分') {
    return content.includes('【结局设计】') &&
           content.includes('【人物命运】');
  } else if (section === '第一部分') {
    return content.includes('【世界观体系设计】') &&
           content.includes('【人物塑造系统】') &&
           content.includes('【核心矛盾设计】');
  }

  return true;
};

/**
 * 使用XMLHttpRequest处理流式响应（降级方案，用于不支持ReadableStream的浏览器）
 * @param {string} API_BASE_URL API基础URL
 * @param {Object} params 请求参数
 * @param {string} section 部分名称
 * @param {Function} onChunkReceived 接收到内容块时的回调函数
 * @param {Function} onStatusUpdate 状态更新回调函数
 * @param {Function} countWords 字数统计函数
 * @param {string} [streamPath='/ai/proxy-ai'] API路径
 * @param {Function} [onThinkingChunk] 思考内容接收回调
 * @returns {Promise<{isComplete: boolean, content: string, wordCount: number, finishReason?: string, sawDone?: boolean}>}
 */
/**
 * 【SSE 特殊事件】辅助创作逆向号池下发的控制事件（非生成内容）
 * - queue_status: 排队让号中（收费任务优先），更新状态提示不计入内容
 * - error: 流内错误（排队超时/生成失败），记录消息供结束后抛错
 * @returns {boolean} true=已处理，调用方应跳过常规内容解析
 */
const handleSpecialSseEvent = (json, onStatusUpdate, errRef) => {
  if (!json || typeof json !== 'object') return false;
  if (json.queue_status === 'waiting') {
    onStatusUpdate(json.message || '号池繁忙，排队等待中...');
    return true;
  }
  if (json.error === true) {
    if (errRef) errRef.msg = json.message || 'AI生成失败';
    onStatusUpdate(`⚠ ${json.message || 'AI生成失败'}`);
    return true;
  }
  return false;
};

const handleStreamWithXHR = (API_BASE_URL, params, section, onChunkReceived, onStatusUpdate, countWords, streamPath = '/assist/ai/proxy-ai', onThinkingChunk = null) => {
  return new Promise((resolve, reject) => {
    let generatedContent = '';
    let wordCount = 0;
    let buffer = '';
    const streamErr = { msg: null }; // 号池排队超时/生成失败的流内错误
    let sawDone = false;
    let lastFinishReason = null;
    const TIMEOUT = 10 * 60 * 1000; // 10分钟超时
    
    // 判断是否是小说大纲生成请求（兼容新旧两种 section 命名）
    const isNovelOutlineRequest =
      params.messages?.find(m => m.role === 'system')?.content?.includes('【小说大纲三部分结构说明】') ||
      params.messages?.find(m => m.role === 'system')?.content?.includes('完整大纲创作指南') ||
      section === 'FULL_OUTLINE';
    
    const apiEndpoint = `${API_BASE_URL}${streamPath.startsWith('/') ? streamPath : `/${streamPath}`}`;
    console.log(`[XHR降级] 使用API端点: ${apiEndpoint}${isNovelOutlineRequest ? ' (小说大纲生成)' : ''}`);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', apiEndpoint, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('authToken') || ''}`);
    xhr.setRequestHeader('Connection', 'keep-alive');
    xhr.setRequestHeader('Cache-Control', 'no-cache');
    
    // 设置超时
    xhr.timeout = TIMEOUT;
    
    let lastActivityTime = Date.now();
    
    // 监控活动状态
    const activityCheckInterval = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityTime;
      if (inactiveTime > 30000) {
        console.log(`[XHR] 流式生成已${inactiveTime/1000}秒无活动，但仍在等待数据...`);
        onStatusUpdate(`连接空闲${Math.floor(inactiveTime/1000)}秒，继续等待...`);
      }
    }, 10000);
    
    // 用于跟踪已处理的文本长度
    let processedLength = 0;
    
    // 使用定时器轮询检查新数据（因为某些浏览器onprogress不触发）
    const pollInterval = setInterval(() => {
      lastActivityTime = Date.now();
      
      try {
        // 获取当前已接收的文本
        const currentText = xhr.responseText || '';
        
        // 只处理新增的部分
        if (currentText.length > processedLength) {
          const newChunk = currentText.slice(processedLength);
          processedLength = currentText.length;
          
          // 将新数据添加到缓冲区
          buffer += newChunk;
          
          // 按行解析SSE格式
          const lines = buffer.split('\n');
          // 保留最后一行（可能是不完整行）
          buffer = lines[lines.length - 1];
          
          for (let i = 0; i < lines.length - 1; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            if (line === 'data: [DONE]') {
              sawDone = true;
              continue;
            }
            
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              try {
                const json = JSON.parse(data);
                if (handleSpecialSseEvent(json, onStatusUpdate, streamErr)) continue;
                const choice = json.choices && json.choices[0];
                // Check for thinking mode special chunks sent by backend
                if (choice?._type === 'thinking' && onThinkingChunk) {
                  const thinkingContent = choice?.delta?.reasoning_content || '';
                  onThinkingChunk(thinkingContent);
                  continue;
                }
                if (choice?._type === 'thinking_end' && onThinkingChunk) {
                  onThinkingChunk(null);
                  continue;
                }
                // 逆向通道换号重试：后端已重置输出，前端同步清空已累计内容
                if (choice?._type === 'reverse_retry') {
                  generatedContent = '';
                  wordCount = 0;
                  onStatusUpdate('逆向通道换号重试中，重新接收内容...');
                  onChunkReceived('', '', 0);
                  continue;
                }
                const content = choice?.delta?.content || choice?.message?.content || '';
                if (typeof choice?.finish_reason !== 'undefined' && choice.finish_reason !== null) {
                  lastFinishReason = choice.finish_reason;
                }
                if (content) {
                  generatedContent += content;
                  wordCount = countWords(generatedContent);
                  onChunkReceived(content, generatedContent, wordCount);
                }
              } catch (e) {
                // 非JSON行，忽略
                console.log('[XHR] 解析JSON失败，跳过:', e);
              }
            }
          }
        }
      } catch (e) {
        console.error('[XHR] 处理数据时出错:', e);
      }
    }, 50); // 每50ms检查一次新数据
    
    // 也监听onprogress事件（如果浏览器支持）
    xhr.onprogress = () => {
      lastActivityTime = Date.now();
    };
    
    xhr.onload = () => {
      clearInterval(activityCheckInterval);
      clearInterval(pollInterval);
      
      if (xhr.status === 402) {
        const error = new Error('余额不足，请充值后继续使用');
        error.status = 402;
        reject(error);
        return;
      }

      if (xhr.status === 429) {
        const error = new Error('今日免费次数已用完，请明天再来');
        error.status = 429;
        reject(error);
        return;
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        // 处理最后的数据
        const finalText = xhr.responseText || '';
        if (finalText.length > processedLength) {
          const remainingChunk = finalText.slice(processedLength);
          buffer += remainingChunk;
          
          const lines = buffer.split('\n');
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            if (line === 'data: [DONE]') {
              sawDone = true;
              continue;
            }
            
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              try {
                const json = JSON.parse(data);
                if (handleSpecialSseEvent(json, onStatusUpdate, streamErr)) continue;
                const choice = json.choices && json.choices[0];
                const content = choice?.delta?.content || choice?.message?.content || '';
                if (typeof choice?.finish_reason !== 'undefined' && choice.finish_reason !== null) {
                  lastFinishReason = choice.finish_reason;
                }
                if (content) {
                  generatedContent += content;
                  wordCount = countWords(generatedContent);
                  onChunkReceived(content, generatedContent, wordCount);
                }
              } catch (e) {
                console.log('[XHR] 解析JSON失败，跳过:', e);
              }
            }
          }
        }

        // 🔥【完成标记兜底】同 ReadableStream 路径：finish_reason=stop 且无流内错误即视为完整
        const isComplete = !!sawDone || (lastFinishReason === 'stop' && !streamErr.msg);
        console.log(`[XHR] 流式生成完成，finish_reason=${lastFinishReason}, sawDone=${sawDone}, 完整=${isComplete}`);

        emitAssistAiQuotaRefresh(streamPath);

        // 流内错误（排队超时/生成失败）且无内容 → 以错误结束，防止上层保存空内容
        if (streamErr.msg && !generatedContent) {
          reject(new Error(streamErr.msg));
          return;
        }

        resolve({
          isComplete,
          content: generatedContent,
          wordCount,
          finishReason: lastFinishReason || undefined,
          sawDone
        });
      } else {
        reject(new Error(`请求失败: ${xhr.status}`));
      }
    };
    
    xhr.onerror = () => {
      clearInterval(activityCheckInterval);
      clearInterval(pollInterval);
      reject(new Error('网络错误'));
    };
    
    xhr.ontimeout = () => {
      clearInterval(activityCheckInterval);
      clearInterval(pollInterval);
      reject(new Error(`请求超时(${TIMEOUT/1000}秒)`));
    };
    
    // 发送请求
    xhr.send(JSON.stringify(params));
  });
};

/**
 * 处理流式生成内容，增强网络容错性，并返回严格的完整性标记
 * @param {string} API_BASE_URL API基础URL
 * @param {Object} params 请求参数（OpenAI兼容 Chat Completions）
 * @param {string} section 部分名称
 * @param {Function} onChunkReceived 接收到内容块时的回调函数 (chunk, fullContent, wordCount) => void
 * @param {Function} onStatusUpdate 状态更新回调函数
 * @param {string} [streamPath='/assist/ai/proxy-ai'] 流式接口路径（统一走辅助创作路由·免费次数额度；旧 '/ai/proxy-ai' 计费路由已弃用）
 * @returns {Promise<{isComplete: boolean, content: string, wordCount: number, finishReason?: string, sawDone?: boolean}>}
 */
export const handleStreamGeneration = async (API_BASE_URL, params, section, onChunkReceived, onStatusUpdate, streamPath = '/assist/ai/proxy-ai', onThinkingChunk = null) => {
  let generatedContent = '';
  let wordCount = 0;
  
  // 计算中文和英文字数
  const countWords = (text) => {
    if (!text) return 0;
    
    // 中文字符数（包括标点）
    const chineseCount = (text.match(/[\u4e00-\u9fa5]|[\u3000-\u303f\uff00-\uffef]/g) || []).length;
    
    // 英文单词数（简单计算，按空格分割）
    const englishText = text.replace(/[\u4e00-\u9fa5]|[\u3000-\u303f\uff00-\uffef]/g, '');
    const englishCount = englishText.split(/\s+/).filter(word => word.length > 0).length;
    
    // 返回总字数
    return chineseCount + englishCount;
  };
  
  try {
    // 🔥 兼容性检查：某些手机浏览器（QQ浏览器、搜狗浏览器）不支持ReadableStream
    // 在发送请求前检查浏览器是否支持ReadableStream
    const supportsReadableStream = typeof ReadableStream !== 'undefined' && 
                                    typeof Response !== 'undefined' && 
                                    Response.prototype.hasOwnProperty('body');
    
    if (!supportsReadableStream) {
      console.warn('浏览器不支持ReadableStream，使用XMLHttpRequest降级方案');
      return handleStreamWithXHR(API_BASE_URL, params, section, onChunkReceived, onStatusUpdate, countWords, streamPath, onThinkingChunk);
    }
    
    // 创建AbortController用于手动中止请求
    const controller = new AbortController();
    const signal = controller.signal;
    
    // 将超时时间拉长到10分钟，避免长输出被提前终止
    const TIMEOUT = 10 * 60 * 1000;
    
    // 发送请求（OpenAI兼容SSE）
    console.log("开始发送流式生成请求...");
    
    // 判断是否是小说大纲生成请求（兼容新旧两种 section 命名）
    const isNovelOutlineRequest =
      params.messages?.find(m => m.role === 'system')?.content?.includes('【小说大纲三部分结构说明】') ||
      params.messages?.find(m => m.role === 'system')?.content?.includes('完整大纲创作指南') ||
      section === 'FULL_OUTLINE';
    
    const apiEndpoint = `${API_BASE_URL}${streamPath.startsWith('/') ? streamPath : `/${streamPath}`}`;
    console.log(`使用API端点: ${apiEndpoint}${isNovelOutlineRequest ? ' (小说大纲生成)' : ''}`);
    
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        'Connection': 'keep-alive',
        'Keep-Alive': 'timeout=600'
      },
      body: JSON.stringify(params),
      signal: signal,
      // 某些运行环境不支持fetch超时参数，安全起见仍然传入
      timeout: TIMEOUT
    });
    
    if (!response.ok) {
      // 处理特定的错误状态码
      if (response.status === 429) {
        const error = new Error('今日免费次数已用完，请明天再来');
        error.status = 429;
        throw error;
      }
      if (response.status === 402) {
        const error = new Error('余额不足，请充值后继续使用');
        error.status = 402;
        throw error;
      }
      throw new Error(`请求失败: ${response.status}`);
    }
    
    // 再次检查response.body是否可用（某些浏览器可能支持ReadableStream但不支持response.body）
    if (!response.body || typeof response.body.getReader !== 'function') {
      console.warn('response.body不可用，切换到XMLHttpRequest降级方案');
      return handleStreamWithXHR(API_BASE_URL, params, section, onChunkReceived, onStatusUpdate, countWords, streamPath, onThinkingChunk);
    }
    
    // 处理流式响应（使用ReadableStream）
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let done = false;
    let buffer = '';
    let lastActivityTime = Date.now();

    // 完整性标记
    let sawDone = false; // 是否收到 [DONE]
    let lastFinishReason = null; // 最后一个 chunk 的 finish_reason
    const streamErr = { msg: null }; // 号池排队超时/生成失败的流内错误
    // 🔥【防卡死修复】流读取连续失败重试：原实现 read 抛错后无限"等5秒重试"永不退出，
    //    导致上层"AI正在生成大纲"状态永久卡住（网络闪断/代理提前断连场景）。
    //    连续失败超过上限（约60秒）即放弃，按已收内容返回（isComplete=false），上层正常复位状态
    let readRetryCount = 0;
    const MAX_READ_RETRIES = 12;
    
    // 设置一个定时器检查活动状态
    // 🔥 空闲提示节流（里程碑式）：AI思考阶段可达1-3分钟，原实现每10秒追加一条
    //    "[连接空闲XX秒，继续等待...]" 会连刷十几条污染大纲文本区。
    //    改为 30秒/2分钟 各提示一次；收到新数据后重置，再次空闲时重新计级。
    let idleNoticeStage = 0;
    const activityCheckInterval = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityTime;
      if (inactiveTime > 120000 && idleNoticeStage < 2) {
        idleNoticeStage = 2;
        console.log(`流式生成已${Math.floor(inactiveTime/1000)}秒无活动，仍在等待数据...`);
        onStatusUpdate('AI仍在深度创作中，长篇内容可能需要几分钟，请耐心等待...');
      } else if (inactiveTime > 30000 && idleNoticeStage < 1) {
        idleNoticeStage = 1;
        onStatusUpdate('AI正在构思中，可能需要1-3分钟，请稍候...');
      }
    }, 10000);
    
    // 设置超时定时器
    const timeoutId = setTimeout(() => {
      console.log(`请求超时(${TIMEOUT/1000}秒)，正在终止连接...`);
      controller.abort();
      clearInterval(activityCheckInterval);
    }, TIMEOUT);
    
    while (!done) {
      try {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        readRetryCount = 0; // 成功读取即重置连续失败计数（网络恢复后重新起算）
        
        // 更新最后活动时间；收到新数据重置空闲提示级别（下段停顿可重新提示）
        lastActivityTime = Date.now();
        idleNoticeStage = 0;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          // SSE按行解析
          const lines = buffer.split('\n');
          for (let i = 0; i < lines.length - 1; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            if (line === 'data: [DONE]') {
              sawDone = true;
              continue;
            }
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              try {
                const json = JSON.parse(data);
                if (handleSpecialSseEvent(json, onStatusUpdate, streamErr)) continue;
                const choice = json.choices && json.choices[0];
                // Check for thinking mode special chunks sent by backend
                if (choice?._type === 'thinking' && onThinkingChunk) {
                  // Forward thinking content to the thinking callback only — do NOT add to generatedContent
                  const thinkingContent = choice?.delta?.reasoning_content || '';
                  onThinkingChunk(thinkingContent);
                  continue;
                }
                if (choice?._type === 'thinking_end' && onThinkingChunk) {
                  // Thinking phase ended — signal to clear thinking display
                  onThinkingChunk(null);
                  continue;
                }
                // 逆向通道换号重试：后端已重置输出，前端同步清空已累计内容
                if (choice?._type === 'reverse_retry') {
                  generatedContent = '';
                  wordCount = 0;
                  onStatusUpdate('逆向通道换号重试中，重新接收内容...');
                  onChunkReceived('', '', 0);
                  continue;
                }
                const content = choice?.delta?.content || choice?.message?.content || '';
                if (typeof choice?.finish_reason !== 'undefined' && choice.finish_reason !== null) {
                  lastFinishReason = choice.finish_reason; // 可能为 stop/length/...
                }
                if (content) {
                  generatedContent += content;
                  wordCount = countWords(generatedContent);
                  onChunkReceived(content, generatedContent, wordCount);
                }
              } catch (e) {
                // 非JSON行，忽略
                console.log('解析JSON失败，跳过:', e);
              }
            }
          }
          // 保留最后一行（可能是不完整行）
          buffer = lines[lines.length - 1];
        }
      } catch (readError) {
        if (readError.name === 'AbortError') {
          console.log('流读取被中止');
          break;
        }
        // 🔥【防卡死修复】网络错误重试上限：连续失败超过 MAX_READ_RETRIES 次即放弃，
        //    返回已收内容（isComplete=false），绝不让 Promise 永不 settle 卡死上层状态
        readRetryCount += 1;
        if (readRetryCount > MAX_READ_RETRIES) {
          console.error(`流读取连续失败${MAX_READ_RETRIES}次，放弃重试，按已收内容结束`);
          try { controller.abort(); } catch (_) {}
          break;
        }
        console.error('流读取错误:', readError);
        onStatusUpdate(`网络连接中断，正在重试（${readRetryCount}/${MAX_READ_RETRIES}）...`);
        await new Promise(resolve => setTimeout(resolve, 5000));
        continue;
      }
    }
    
    clearTimeout(timeoutId);
    clearInterval(activityCheckInterval);
    
    // 🔥【完成标记兜底】个别场景（代理缓冲/断连时序）[DONE] 行可能丢失，
    //    若已收到 finish_reason=stop 且无流内错误，视为正常完成，避免误报"生成未完成"
    const isComplete = !!sawDone || (lastFinishReason === 'stop' && !streamErr.msg);
    console.log(`流式生成完成，finish_reason=${lastFinishReason}, sawDone=${sawDone}, 完整=${isComplete}`);

    emitAssistAiQuotaRefresh(streamPath);

    // 流内错误（排队超时/生成失败）且无内容 → 以错误结束，防止上层保存空内容
    if (streamErr.msg && !generatedContent) {
      throw new Error(streamErr.msg);
    }

    return {
      isComplete,
      content: generatedContent,
      wordCount,
      finishReason: lastFinishReason || undefined,
      sawDone
    };
    
  } catch (error) {
    console.error('流式生成发生错误:', error);
    onStatusUpdate(`生成出错: ${error.message}`);
    
    // 出错返回不完整标记
    return {
      isComplete: false,
      content: generatedContent,
      wordCount
    };
  }
};

const streamCompletionHandler = {
  verifyContentCompleteness,
  handleStreamGeneration
};

export default streamCompletionHandler;